import { motion } from 'framer-motion';
import { Venus, Mars, Sparkles } from 'lucide-react';

import { Card, cn } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import type { CoachGender } from '../../../stores/coach.store';
import { useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';

const GENDERS: { id: CoachGender; icon: typeof Venus }[] = [
  { id: 'female', icon: Venus },
  { id: 'male', icon: Mars },
  { id: 'nb', icon: Sparkles },
];

interface GenderStepProps {
  value: CoachGender | null;
  onSelect: (gender: CoachGender) => void;
}

export function GenderStep({ value, onSelect }: GenderStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorGender);
  const { strings, interp } = useStrings();
  const stepLabel = interp(strings.onboarding.stepLabel, {
    current: 1,
    total: strings.onboarding.totalSteps,
  });
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-6"
    >
      <header className="space-y-2 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{stepLabel}</p>
        <h2 className="font-serif text-3xl">{strings.onboarding.gender.title}</h2>
        <p className="text-sm text-muted-foreground">
          {strings.onboarding.gender.hint}
        </p>
      </header>

      <div className="grid gap-3">
        {GENDERS.map(({ id, icon: Icon }) => {
          const meta = strings.coach.genders[id];
          const active = value === id;
          return (
            <Card
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(id)}
              className={cn(
                'flex cursor-pointer items-center gap-4 border-border bg-card p-5 transition-all hover:border-primary/60',
                active && 'border-primary glow-primary',
              )}
            >
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full bg-muted text-primary',
                  active && 'bg-primary text-primary-foreground',
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">{meta.label}</div>
                <div className="text-xs text-muted-foreground">{meta.hint}</div>
              </div>
            </Card>
          );
        })}
      </div>
    </motion.div>
  );
}
